import { defaultCurrency, defaultLocaleForMoney, defaultPrecision } from '@/config/money'

export interface MoneyOptions {
  // Inputs for formatMoney().

  amount: number
  // Amount in minor currency units (e.g. cents).

  currency?: string
  // ISO 4217 currency code. Default from config.

  locale?: string
  // Locale for formatting (e.g. en-US). Default from config.

  precision?: number
  // Decimal places for the minor unit (also used as the divisor exponent: `major = amount / 10 ** precision`). Default from config.

  fractionDigits?: number
  // Override for the number of fraction digits shown by `Intl.NumberFormat`. When omitted, falls back to `precision` so behavior is unchanged. Use this when the divisor (`precision`) and the displayed decimal places need to differ — e.g. precision: 0 to display the raw amount, fractionDigits: 2 to render it as `18.00`.

  includeCurrency?: boolean
  // When true, prefix with the currency symbol (`narrowSymbol`, e.g. `$` not `US$`). Default false so table cells can show numbers only while the column header carries the symbol.
}

/**
 * Narrow currency symbol for the default currency, e.g. `Br` for BYN.
 *
 * Computed once at module load from the locale active at that moment (not
 * reactive to a later language switch) — the symbol depends on `currency`,
 * which isn't user-switchable yet, so this stays a plain constant.
 */
export const currencySymbol: string =
  new Intl.NumberFormat(defaultLocaleForMoney.value, {
    style: 'currency',
    currency: defaultCurrency,
    currencyDisplay: 'narrowSymbol',
  })
    .formatToParts(0)
    .find((p) => p.type === 'currency')?.value ?? defaultCurrency

/**
 * Column title with currency in the header only, e.g. `Balance (Br)` for BYN.
 */
export function moneyColumnLabel(
  baseLabel: string,
  currency = defaultCurrency,
  locale = defaultLocaleForMoney.value,
): string {
  const symbol =
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? currency
  return `${baseLabel} (${symbol})`
}

/**
 * Format a money amount: decimal only by default; set `includeCurrency: true` for a full currency string.
 */
export function formatMoney(options: MoneyOptions): { formatted: string } {
  const {
    amount,
    currency = defaultCurrency,
    locale = defaultLocaleForMoney.value,
    precision = defaultPrecision,
    fractionDigits,
    includeCurrency = false,
  } = options

  const major = amount / 10 ** precision
  const displayDigits = fractionDigits ?? precision
  const formatted = includeCurrency
    ? new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        // Only override when the caller explicitly set fractionDigits — otherwise let
        // Intl pick the currency's natural digits (e.g. 2 for USD, 0 for JPY).
        ...(fractionDigits != null
          ? {
              minimumFractionDigits: fractionDigits,
              maximumFractionDigits: fractionDigits,
            }
          : {}),
      }).format(major)
    : new Intl.NumberFormat(locale, {
        minimumFractionDigits: displayDigits,
        maximumFractionDigits: displayDigits,
      }).format(major)

  return { formatted }
}
